import { parseDate, roundToXDecimals } from '@ais-ui/utils';
import { QueryPointResult } from './queryPointResult';

export interface AtonTargetInfo {
  assignedMode: string;
  atonType: string;
  country: string;
  flag: string;
  lastAisReport: Date | null;
  lastAisLatitude: string;
  lastAisLongitude: string;
  lastAisSatLatitude: string;
  lastAisSatLongitude: string;
  lastAisSatReport: Date | null;
  latitudeDegrees: string;
  longitudeDegrees: string;
  latitude: number;
  longitude: number;
  length: string;
  mmsi: string;
  name: string;
  offPosition: string;
  state: string;
  status: string;
  targetType: string;
  width: string;
  analogInternal: string;
  analogExternal1: string;
  analogExternal2: string;
  statusInternal: string;
  statusExternal: string;
}

const desiredTargetIds: string[] = [
  'assigned_mode',
  'country',
  'flag',
  'length',
  'mmsi',
  'name',
  'off_position',
  'state',
  'status',
  'type',
  'width',
  'analog_int',
  'analog_ext1',
  'analog_ext2',
  'status_int',
  'status_ext',
  // New IDs
  'datetime_terrestrial_last_report',
  'datetime_satellite_last_report',
  'datetime_last_report',
  'lat_terrestrial_last_report',
  'lon_terrestrial_last_report',
  'lat_satellite_last_report',
  'lon_satellite_last_report',
  'lat_last_report',
  'lon_last_report',
  'positionlat',
  'positionlon',
  // Old IDs (backward compatibility)
  'lastaisreport',
  'lastaissatreport',
];

const desiredTargetNames: string[] = [
  // New names
  'Last Terrestrial Latitude',
  'Last Terrestrial Longitude',
  'Last Satellite Latitude',
  'Last Satellite Longitude',
  'Last Latitude',
  'Last Longitude',
  // Old names (backward compatibility)
  'Last AIS Latitude',
  'Last AIS Longitude',
  'Last AIS-Sat Latitude',
  'Last AIS-Sat Longitude',
  'Latitude',
  'Longitude',
];

export function extractAtonTargetInfos(
  result: QueryPointResult,
): AtonTargetInfo[] {
  const atonTargetInfos: AtonTargetInfo[] = [];
  for (const target of result.targetInfo) {
    const rawAtonTargetInfo: { [key: string]: string } = {};
    for (const target_value of target.values) {
      // Here we map values by their ID
      if (desiredTargetIds.includes(target_value.id)) {
        rawAtonTargetInfo[target_value.id] = target_value.value;
      }
      // Some values have the same ID so here we map those by the NAME instead
      if (desiredTargetNames.includes(target_value.name)) {
        rawAtonTargetInfo[target_value.name] = target_value.value;
      }
    }
    const mmsi = target['mmsi'] || rawAtonTargetInfo['MMSI'];

    const AtonInfo: AtonTargetInfo = {
      assignedMode: rawAtonTargetInfo['assigned_mode'],
      atonType: rawAtonTargetInfo['type'],
      country: rawAtonTargetInfo['country'],
      flag: rawAtonTargetInfo['flag'],
      lastAisReport: parseDate(
        rawAtonTargetInfo['datetime_terrestrial_last_report'] ??
          rawAtonTargetInfo['lastaisreport'],
      ),
      lastAisLatitude:
        rawAtonTargetInfo['Last Terrestrial Latitude'] ??
        rawAtonTargetInfo['lat_terrestrial_last_report'] ??
        rawAtonTargetInfo['Last AIS Latitude'],
      lastAisLongitude:
        rawAtonTargetInfo['Last Terrestrial Longitude'] ??
        rawAtonTargetInfo['lon_terrestrial_last_report'] ??
        rawAtonTargetInfo['Last AIS Longitude'],
      lastAisSatLatitude:
        rawAtonTargetInfo['Last Satellite Latitude'] ??
        rawAtonTargetInfo['lat_satellite_last_report'] ??
        rawAtonTargetInfo['Last AIS-Sat Latitude'],
      lastAisSatLongitude:
        rawAtonTargetInfo['Last Satellite Longitude'] ??
        rawAtonTargetInfo['lon_satellite_last_report'] ??
        rawAtonTargetInfo['Last AIS-Sat Longitude'],
      lastAisSatReport: parseDate(
        rawAtonTargetInfo['datetime_satellite_last_report'] ??
          rawAtonTargetInfo['lastaissatreport'],
      ),
      latitudeDegrees:
        rawAtonTargetInfo['positionlat'] ?? rawAtonTargetInfo['Latitude'],
      longitudeDegrees:
        rawAtonTargetInfo['positionlon'] ?? rawAtonTargetInfo['Longitude'],
      latitude: target['lat']
        ? roundToXDecimals(target['lat'], 4)
        : target['lat'],
      longitude: target['lon']
        ? roundToXDecimals(target['lon'], 4)
        : target['lon'],
      length: rawAtonTargetInfo['length'],
      mmsi: mmsi,
      name: rawAtonTargetInfo['name'],
      offPosition: rawAtonTargetInfo['off_position'],
      state: rawAtonTargetInfo['state'],
      status: rawAtonTargetInfo['status'],
      targetType: target['type'],
      width: rawAtonTargetInfo['width'],
      analogInternal: rawAtonTargetInfo['analog_int'],
      analogExternal1: rawAtonTargetInfo['analog_ext1'],
      analogExternal2: rawAtonTargetInfo['analog_ext2'],
      statusInternal: rawAtonTargetInfo['status_int'],
      statusExternal: rawAtonTargetInfo['status_ext'],
    };
    atonTargetInfos.push(AtonInfo);
  }

  return atonTargetInfos;
}
